import { useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { FaCheck, FaClipboardList, FaCopy, FaInfoCircle, FaUndo } from 'react-icons/fa';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { Button } from '@prosolve/common-components-ui'
import { getClientSubdomain, isStarConsultation } from '../../utils';

const SessionBlock = styled.div`
  display: flex;
  background: #F3FBFF;
  color: #565656;
  padding: 7px 10px 7px 7px;
  border: 1px solid #c4c4c4;
  border-radius: 5px;
  font-size: 16px;
  word-wrap: break-word;
  white-space: normal;
`;

const SessionButton = styled(Button)`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px;

  & div { 
    font-size: 10px;
  }
`

const DynamicInputWrapper = styled.div`
  position: relative;
  left: -5px;
  border-radius: 5px;
  color: #565656;
  display: flex;
  align-items: center;
  padding: 5px 5px;
  gap: 5px;

  &:focus-within {
    background: rgba(0,0,0,0.05)
  }

  ${({ $isEditable }) => $isEditable && css`
    cursor: pointer;

    &:hover {
      background: rgba(0,0,0,0.05)
    }
  `}
`

const DynamicInput = styled.input`
  border: none;
  background: transparent;
  flex: 1;
  font-family: ProximaSoft-Regular, "Open Sans", sans-serif;
  font-size: 16px;
  outline: none;
  margin: 0;
  padding: 2px 5px;

  ${({ $isEditable }) => $isEditable && css`
    border-bottom: solid 2px silver;
  `}
`

const FadeDiv = styled.div`
  opacity: 1;
  transition: opacity 0.1s ease-out; 

  &.hidden {
    opacity: 0;
    transition: opacity 0.3s ease-in;
  }
`

const FieldSaveIcon = ({ sessionData, field, isUpdateLoading }) => {
  const { _id } = sessionData
  const fadeElementRef = useRef()
  const timeout = useRef()
  const isCurrentField = isUpdateLoading?.some(session => (session?._id === _id && Object.hasOwn(session, field)))

  useEffect(() => {
    if (isCurrentField) {
      const el = fadeElementRef.current

      clearTimeout(timeout.current)
      el.classList.remove('hidden')

      timeout.current = setTimeout(() => {
        el.classList.add('hidden')
      }, 1000)
    }
  }, [isCurrentField])

  return <>
    <FadeDiv className='hidden' ref={fadeElementRef}>
      <div style={{ alignItems: 'center', display: 'flex', gap: '5px', color: 'green' }}>
        <FaCheck />
        <span>Saved</span>
      </div>
    </FadeDiv>
  </>
}

export default ({
  sessionData: sessionDataParam,
  eventData = {},
  isShowEdit,
  onUpdate,
  isUpdateLoading,
  onCopy,
  copiedSession
}) => {
  const navigate = useNavigate()

  const [sessionData, setSessionData] = useState(sessionDataParam)
  const [lastSessionSave, setLastSessionSave] = useState(sessionDataParam)
  const [isEdit, setIsEdit] = useState()

  const {
    _id,
    name,
    startTime,
    endTime
  } = sessionData
  
  const clientSubdomain = getClientSubdomain()
  const isStarSubdomain = (clientSubdomain === 'star')

  const isStarConsultationSession = isStarSubdomain && isStarConsultation(eventData)
  const canReset = (lastSessionSave !== sessionDataParam)

  const displayFields = isStarSubdomain 
    ? 
    (
      isStarConsultationSession ?
      [
        {
          name: 'Name of School',
          field: 'schoolName'
        },
        {
          name: 'Address',
          field: 'address'
        },
        {
          name: 'Name of Teacher',
          field: 'teacherName'
        },
        {
          name: 'Teacher Email',
          field: 'teacherEmail'
        },
        {
          name: 'Notes',
          field: 'notes'
        },
        {
          name: 'Classroom Type',
          field: 'classroomType'
        },
      ]
      :
      []
    )
    : [
      {
        name: 'Location',
        field: 'location'
      },
      {
        name: 'Trainer Lead',
        field: 'trainerLeadName'
      }
  ]

  const blockMinHeight = isStarSubdomain ? (isStarConsultationSession ? '250px' : '0px') : '125px'

  const formatSessionsTimes = (startTime, endTime) => {
    const start = moment(startTime, 'hh:mm:ss').format('h:mm A');
    const end = moment(endTime, 'hh:mm:ss').format('h:mm A');
    return `${start} - ${end}`;
  }

  const onClickSessionLink = (session = {}) => {
    navigate(`session?id=${session._id}`);
  }

  const handleClick = (evt, field) => {
    evt.currentTarget.querySelector('input').focus()
    setIsEdit(field)
  }

  const handleChange = (evt, field) => {
    const newSessionData = { ...sessionData }
    newSessionData[field] = evt.target.value
    setSessionData(newSessionData)
  }

  const handleUpdate = (evt, field) => {
    if (lastSessionSave[field] !== evt.target.value) {
      const updateSessionData = { _id: sessionData._id }
      updateSessionData[field] = evt.target.value
      onUpdate(updateSessionData)
      setLastSessionSave({ ...sessionData, ...updateSessionData })
    }
    setIsEdit(evt?.relatedTarget?.classList.contains('editable'))
  }

  const handleReset = () => {
    setSessionData(sessionDataParam)
    onUpdate(sessionDataParam)
    setLastSessionSave(sessionDataParam)
  }

  const handlePaste = () => {
    const { _id, name, startTime, endTime } = sessionData
    const { schoolName = '', address = '', teacherName = '', teacherEmail = '', notes = '', classroomType = '' } = copiedSession
    const updateSessionData = { 
      _id, name, startTime, endTime, 
      schoolName, address, teacherName, teacherEmail, notes, classroomType 
    }
    setSessionData(updateSessionData)
    onUpdate(updateSessionData)
    setLastSessionSave({ ...sessionData, ...updateSessionData })
  }

  return (
    <SessionBlock key={_id} style={{ minHeight: blockMinHeight }}>
      <div style={{ background: '#0280c6', width: '3px' }}>
        &nbsp;
      </div>
      <div style={{ flex: 1, padding: '3px 0 3px 10px' }}>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px' }}>{formatSessionsTimes(startTime, endTime)}</div>
            <div
              style={{ display: 'inline-flex', marginTop: '5px', color: '#0280c6', fontWeight: 'bold', fontSize: '16px', textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => onClickSessionLink(sessionData)}
            >
              {name}
            </div>
            {isShowEdit &&
              <div style={{ alignItems: 'center', display: 'flex', gap: '5px', fontSize: '14px', fontWeight: 'bold', fontStyle: 'italic', paddingTop: '8px' }}>
                <FaInfoCircle style={{ color: 'rgb(2, 128, 198)' }} />
                <div>Click on the fields below to edit</div>
              </div>
            }
          </div>
          {isShowEdit && isStarConsultationSession &&
            <div style={{ display: 'flex', gap: '5px' }}>
              {/*
              <Button $colored={true} onClick={() => setIsShowEditModal(session)}>Edit</Button>
              */}
              <SessionButton $colored={canReset} $disabled={!canReset} onClick={() => canReset && handleReset()}
                 title={'Resets any session changes back to when the page was first opened'}
              >
                <FaUndo />
                <div>Reset</div>
              </SessionButton>
              <SessionButton $colored={copiedSession?._id !== _id} onClick={() => onCopy(sessionData)}
                title={'Copies all session information (which can be applied using the Paste button)'}
              >
                <FaCopy />
                <div>Copy</div>
              </SessionButton>
              <SessionButton $colored={true} $disabled={!copiedSession} onClick={() => copiedSession && handlePaste()}
                title={'Replaces all session information with the current copied session'}
              >
                <FaClipboardList />
                <div>Paste</div>
              </SessionButton>
            </div>
          }
        </div>
        {
          displayFields.length > 0 &&
          <div style={{
            borderTop: '1px solid #c4c4c4',
            marginTop: '10px',
            paddingTop: '10px',
            paddingBottom: '5px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {
              displayFields.map(({ name, field }) => {
                return (
                  <DynamicInputWrapper 
                    key={field} 
                    $isEditable={isShowEdit}
                    onClick={(e) => isShowEdit && handleClick(e, field)}
                  >
                    <div style={{ fontWeight: 'bold' }}>
                      {`${name}:`}
                    </div>
                    <DynamicInput 
                      name={field} 
                      value={sessionData[field]}
                      readOnly={!(isShowEdit && isEdit && isStarSubdomain)} 
                      className='editable' 
                      placeholder={'-'}
                      $isEditable={isShowEdit}
                      onChange={(e) => isShowEdit && handleChange(e, field)}
                      onBlur={(e) => isShowEdit && handleUpdate(e, field)}
                    />
                    <FieldSaveIcon 
                      sessionData={sessionData} 
                      field={field} 
                      isUpdateLoading={isUpdateLoading} 
                    />
                  </DynamicInputWrapper>
                )
              })
            }
          </div>
        }
      </div>
    </SessionBlock>
  )
}